import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { showSnackBar } from '../../../components/Snackbar/Snackbar'
import { formatBusinessDate } from '../../../utils/date'
import { formatMoney } from '../../../utils/amount'
import { setFinancingMark } from '../../../features/customer/customerSlice'
import { CHIP, STYLES, FinancingIcon, daysSince, pendingLook } from './FinancingChip'

// WS 2311820. Some Solar clients finance the fee through a lender. The rep turns the file's
// "Financing" mark on or off; nothing is financed by default. `financingState`
// comes from the server (GET /customUsers/:id): pending while a financed client still owes the fee,
// paid once staff record the money (that badge is FinancedBadge's). While the mark is on, card/bank
// charges pause (server); the rep is told before pausing, and before turning it off when payments
// came due meanwhile: those stay skipped in Daily Processing for a manager.
// The day count, colours and icon are shared with the Customers-list row chip (FinancingChip).
export { daysSince }

const FinancingStatusBadge = ({ userId, financingState, canMarkPaid = false, onMarkPaid, onChanged }) => {
  const dispatch = useDispatch()
  const [open, setOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [confirming, setConfirming] = useState(null)

  if (!financingState || financingState.state === 'paid') return null

  const chargeable = financingState.chargeable || 0
  const cameDue = financingState.overdue || 0

  const save = async (financed) => {
    setSaving(true)
    try {
      await dispatch(setFinancingMark({ userId, financed })).unwrap()
      showSnackBar('success', financed
        ? (chargeable > 0 ? 'Marked as financing. Card/bank charges are paused.' : 'Marked as financing')
        : 'Marked as not financing')
      setOpen(false)
      setConfirming(null)
      if (onChanged) onChanged()
    } catch (error) {
      showSnackBar('error', typeof error === 'string' ? error : 'Could not save')
    } finally {
      setSaving(false)
    }
  }

  const chip = `${CHIP} cursor-pointer disabled:opacity-50`

  const panel = 'absolute left-0 top-full mt-2 z-20 w-80 p-4 rounded-xl border border-divider bg-paper shadow-lg text-sm font-normal text-text-primary'

  if (!financingState.financed) {
    return (
      <span className="relative inline-block">
        <button type="button" disabled={saving} className={`${chip} ${STYLES.unmarked}`}
          onClick={() => (chargeable > 0 ? setConfirming((prev) => (prev ? null : 'mark')) : save(true))}>
          Mark as financing
        </button>
        {confirming === 'mark' && (
          <div className={panel}>
            <div className="text-warning-main font-semibold">⚠ {chargeable} card/bank payments will be paused</div>
            <div className="text-text-secondary mt-1">Nothing is charged until the financing is paid or turned off.</div>
            <button type="button" disabled={saving} onClick={() => save(true)}
              className="mt-3 px-3 py-1.5 rounded-lg bg-primary-main text-white font-semibold disabled:opacity-50">
              Pause charges and mark
            </button>
          </div>
        )}
      </span>
    )
  }

  const pending = financingState.state === 'pending'
  const expected = typeof financingState.amount === 'number' ? formatMoney(financingState.amount) : null
  const look = pending && pendingLook(financingState.since)
  const label = look ? [look.title, expected, look.day].filter(Boolean).join(' · ') : 'Financing'
  const style = look ? look.style : STYLES.marked

  return (
    <span className="relative inline-block">
      <button type="button" onClick={() => setOpen((prev) => !prev)} className={`${chip} ${style}`}>
        <FinancingIcon />
        {label}
      </button>
      {open && (
        <div className={panel}>
          <div className="font-bold mb-1">{pending ? 'Financing pending' : 'Financing'}</div>
          <div className="text-text-secondary">
            {pending
              ? [financingState.since && `Signed ${formatBusinessDate(financingState.since)}`, expected && `Expected ${expected}`].filter(Boolean).join(' · ')
              : 'The client is financing the fee. It shows "Financing pending" once the fee is due.'}
          </div>
          {chargeable > 0 && (
            <div className="text-text-secondary mt-1">Card/bank charges paused ({chargeable} payments).</div>
          )}
          {confirming === 'unmark' && (
            <div className="mt-3 text-warning-main">
              ⚠ {cameDue} payments came due while paused. They stay skipped in Daily Processing until a manager restores or reschedules them.
            </div>
          )}
          <div className="flex flex-wrap gap-2 mt-3">
            {pending && canMarkPaid && (
              <button type="button" disabled={saving}
                onClick={() => { setOpen(false); onMarkPaid({ amount: financingState.amount }) }}
                className="px-3 py-1.5 rounded-lg bg-success-main text-white font-semibold disabled:opacity-50">
                Mark paid
              </button>
            )}
            {confirming === 'unmark' ? (
              <button type="button" disabled={saving} onClick={() => save(false)}
                className="px-3 py-1.5 rounded-lg border border-divider text-text-primary disabled:opacity-50">
                Turn Financing off
              </button>
            ) : (
              <button type="button" disabled={saving} onClick={() => (cameDue > 0 ? setConfirming('unmark') : save(false))}
                className="px-3 py-1.5 rounded-lg border border-divider text-text-primary disabled:opacity-50">
                Not financing
              </button>
            )}
          </div>
          {pending && canMarkPaid && (
            <div className="text-xs text-text-secondary mt-2">Mark paid when the money is in: it opens Record financing.</div>
          )}
        </div>
      )}
    </span>
  )
}

export default FinancingStatusBadge
